import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MdArrowBack, MdCheckCircle, MdHourglassEmpty, MdHourglassFull } from "react-icons/md";
import Input from "../../components/input";
import FormRender from "../../components/FormRender";
import axios from "axios";
import { toast } from "react-toastify";
import useAxiosInstance from "../../hooks/useAxios";
import { ATLAS_SEARCH_FORM1 ,ATLAS_SEARCH_FORM2 } from "../../helper";

const defaultDoc = {
  function_name: "",
  function_path: "",
  function_description: "",
  function_long_description: "",
  metadata: {
    project: "",
    module: "",
    analyzed_date: "2999-12-31T00:00:00Z",
    file_extension: "",
    category: "",
    has_docstring: false,
    has_parameters: false,
    function_signature: ""
  },
  search_tags: []
};

const normalizeIsoDateString = (value) => {
  if (!value || typeof value !== 'string') return value;
  let normalized = value.trim();
  normalized = normalized.replace(/\.(\d{3})\d+$/, '.$1');
  const hasTimezone = /[zZ]$|[\+\-]\d{2}:?\d{2}$/.test(normalized);
  if (!hasTimezone) normalized = normalized + 'Z';
  return normalized;
};


function setNestedValue(obj, path, value) {
  const keys = path.split('.')
  const clone = JSON.parse(JSON.stringify(obj));
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (!(k in cur) || typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

const EditFunction = () => {
  const axiosInstance = useAxiosInstance();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const function_id = searchParams.get("function_id");

  const [steps, setSteps] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(defaultDoc);
  const [tagsInput, setTagsInput] = useState("");

  useEffect(() => {
    if (!function_id) return;
    setIsLoading(true);
    axiosInstance
      .get(`/integrations/tech-search/${function_id}/`)
      .then((res) => {
        const data = res?.data || {};
        setFormData({
          function_name: data?.function_name || "",
          function_path: data?.function_path || "",
          function_description: data?.function_description || "",
          function_long_description: data?.function_long_description || "",
          metadata: {
            project: data?.metadata?.project || "",
            module: data?.metadata?.module || "",
            analyzed_date: normalizeIsoDateString(data?.metadata?.analyzed_date) || "2999-12-31T00:00:00Z",
            file_extension: data?.metadata?.file_extension || "",
            category: data?.metadata?.category || "",
            has_docstring: Boolean(data?.metadata?.has_docstring),
            has_parameters: Boolean(data?.metadata?.has_parameters),
            function_signature: data?.metadata?.function_signature || "",
          },
          search_tags: Array.isArray(data?.search_tags) ? data?.search_tags : [],
        });
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to load function");
        setIsLoading(false);
      });
  }, [function_id]);

  const addTag = () => {
    const val = tagsInput.trim();
    if (!val) return;
    if ((formData.search_tags || []).includes(val)) {
      setTagsInput("");
      return;
    }
    setFormData((prev) => ({ ...prev, search_tags: [...(prev.search_tags || []), val] }));
    setTagsInput("");
  };

  const removeTag = (tag) => {
    setFormData((prev) => ({ ...prev, search_tags: (prev.search_tags || []).filter((t) => t !== tag) }));
  };

  const onSubmit = () => {
    const payload = { ...formData };
    // Ensure analyzed_date remains valid ISO
    if (payload?.metadata?.analyzed_date) {
      payload.metadata.analyzed_date = normalizeIsoDateString(payload.metadata.analyzed_date);
    }
    setIsLoading(true);
    const req = function_id
      ? axiosInstance.put(`/integrations/tech-search/${function_id}/`, payload)
      : axiosInstance.post(`/integrations/tech-search/create/`, payload);
    req
      .then(() => {
        toast.success(function_id ? "Function updated" : "Function created");
        setIsLoading(false);
        navigate(`/atlassearch`);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to save function");
        setIsLoading(false);
      });
  };

  const onDelete = () => {
    if (!function_id) return;
    if (!window.confirm("Delete this function?")) return;
    setIsLoading(true);
    axiosInstance
      .delete(`/integrations/tech-search/${function_id}/`)
      .then(() => {
        toast.success("Function deleted");
        setIsLoading(false);
        navigate(`/atlassearch/all`);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to delete");
        setIsLoading(false);
      });
  };

  return (
    <div className="">
      <div className="flex justify-center">
        <div className="w-[680px] flex items-center mt-10">
          <div className="w-18 flex flex-col justify-center items-center">
            {steps <= 0 ? <MdHourglassEmpty /> : <MdCheckCircle />}
            <div className="text-xs px-2 text-center">Base</div>
          </div>
          <div className={`w-full h-1 ${steps <= 0 ? "bg-slate-300" : "bg-black"}`}></div>
          <div className="w-18 flex flex-col justify-center items-center">
            {steps <= 1 ? <MdHourglassEmpty /> : <MdHourglassFull />}
            <div className="text-xs px-2 text-center">Metadata</div>
          </div>
          <div className={`w-full h-1 ${steps <= 1 ? "bg-slate-300" : "bg-black"}`}></div>
          <div className="w-18 flex flex-col justify-center items-center">
            {steps <= 2 ? <MdHourglassEmpty /> : <MdHourglassFull />}
            <div className="text-xs px-2 text-center">Tags</div>
          </div>
          <div className={`w-full h-1 ${steps <= 2 ? "bg-slate-300" : "bg-black"}`}></div>
          <div className="w-18 flex flex-col justify-center items-center">
            {steps <= 3 ? <MdHourglassEmpty /> : <MdHourglassFull />}
            <div className="text-xs px-2 text-center">Preview</div>
          </div>
        </div>
      </div>

      <div className={` w-full justify-center p-5 flex`}>
        {steps === 0 && (
          <div className="w-[676px] p-3 mt-5 border rounded-lg bg-white">
            <p className="text-2xl font-semibold mb-4">{function_id ? "Edit" : "Create"} Function</p>
            <FormRender formData={formData} setFormData={setFormData} formSettings={ATLAS_SEARCH_FORM1} />
            <button
              disabled={isLoading}
              className="flex gap-1 items-center px-2 py-1 mt-2 border border-black bg-black disabled:bg-[#b6b6b6] disabled:text-white text-white rounded-lg"
              onClick={() => setSteps((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}

        {steps === 1 && (
          <div className="w-[676px] p-3 mt-5 border rounded-lg bg-white">
            <p className="text-2xl font-semibold mb-4">Metadata</p>
            <FormRender formData={formData} setFormData={setFormData} formSettings={ATLAS_SEARCH_FORM1} />
            <div className="flex gap-x-2 items-center">
              <button title="Go Back" className="flex gap-1 items-center px-2 py-1 mt-2 border rounded-lg" onClick={() => setSteps((p) => p - 1)}>
                <MdArrowBack />
              </button>
              <button disabled={isLoading} className="flex gap-1 items-center px-2 py-1 mt-2 border border-black bg-black disabled:bg-[#b6b6b6] disabled:text-white text-white rounded-lg" onClick={() => setSteps((p) => p + 1)}>
                Next
              </button>
            </div>
          </div>
        )}

        {steps === 2 && (
          <div className="w-[676px] p-3 mt-5 border rounded-lg bg-white">
            <p className="text-2xl font-semibold mb-4">Tags</p>
            <div className="text-sm font-semibold mb-2">Search tags</div>
            <div className="flex gap-2">
              <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="Type a tag and press Add" />
              <button className="bg-black text-white rounded-lg px-2 py-1 text-sm" onClick={addTag}>Add</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(formData.search_tags || []).map((t) => (
                <span key={t} className="px-2 py-1 rounded-full bg-slate-200 text-sm">
                  {t}
                  <button className="ml-2 underline" onClick={() => removeTag(t)}>remove</button>
                </span>
              ))}
            </div>
            <div className="flex gap-x-2 items-center">
              <button title="Go Back" className="flex gap-1 items-center px-2 py-1 mt-2 border rounded-lg" onClick={() => setSteps((p) => p - 1)}>
                <MdArrowBack />
              </button>
              <button disabled={isLoading} className="flex gap-1 items-center px-2 py-1 mt-2 border border-black bg-black disabled:bg-[#b6b6b6] disabled:text-white text-white rounded-lg" onClick={() => setSteps((p) => p + 1)}>
                Next
              </button>
            </div>
          </div>
        )}

        {steps === 3 && (
          <div className="w-[676px] p-3 mt-5 border rounded-lg bg-white">
            <p className="text-2xl font-semibold mb-4">Preview</p>
            <div className="border rounded-xl bg-gray-50 px-3 py-2">
              <pre style={{ overflowY: 'scroll', maxWidth: '100%', height: '70vh', whiteSpace: 'pre-wrap' }}>{JSON.stringify(formData, null, 2)}</pre>
            </div>
            <div className="flex justify-between">
              <button title="Go Back" className="flex gap-1 items-center px-2 py-1 mt-2 border rounded-lg" onClick={() => setSteps((p) => p - 1)}>
                <MdArrowBack />
              </button>
              <div className="flex items-center gap-2">
                {function_id && (
                  <button disabled={isLoading} className="px-2 py-1 mt-2 border rounded-lg" onClick={onDelete}>Delete</button>
                )}
                <button disabled={isLoading} className="flex gap-1 items-center px-2 py-1 mt-2 border border-black bg-black disabled:bg-[#b6b6b6] disabled:text-white text-white rounded-lg" onClick={onSubmit}>
                  {isLoading ? "Saving..." : (function_id ? "Update" : "Create")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditFunction;


